/**
 * Work hub tasks endpoint — per-user to-do list (mockup v18 r3454-3482).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/tasks.py`` (``GET /tasks/today``,
 *     ``POST /tasks``, ``POST /tasks/{task_id}/complete``,
 *     ``POST /tasks/{task_id}/uncomplete``) — May 2026.
 *
 * Distinct from:
 *   - ``actions.ts``     — HITL AI proposals ("Suggeriti da Ando").
 *   - ``inbox.ts``       — cross-channel message feed.
 *
 * Three sections coexist on the Work hub surface; this helper backs the
 * lower two ("Da fare oggi" + "Completate · oggi"), the top one keeps
 * reading from ``listPendingActions``.
 */

import { apiClient } from "../ApiClient.js";

/** Priority tier — drives both ordering and the FE dot colour. */
export type TaskPriority = "high" | "normal" | "low";

/** Where the task came from — drives the optional "Da {source}" chip. */
export type TaskSource = "user" | "agent" | "email_followup" | "report";

/** Auto-completion evidence emitted by ``TaskAutoCompleter`` (Jun 10, 2026).
 *
 * Persisted in ``tasks.completion_evidence`` JSONB and read by the FE to
 * render the "Completato da Ando" tooltip. The ``matcher_name`` flows
 * to the rollback dampening flow when the user clicks "Era sbagliato?".
 */
export interface TaskCompletionEvidence {
  readonly task_id: string;
  readonly matcher_name: string;
  readonly confidence: number;
  readonly signal_type: string;
  readonly signal_ref: string | null;
  readonly summary: string;
  /** ISO timestamp set by the BE at matcher firing time. */
  readonly matched_at: string;
}

/** Wire shape mirroring ``TaskItem`` on the BE. */
export interface TaskItem {
  readonly task_id: string;
  readonly title: string;
  readonly notes: string | null;
  readonly priority: TaskPriority;
  /** ISO UTC timestamp; ``null`` = no specific due time. */
  readonly due_at: string | null;
  readonly estimated_minutes: number | null;
  /** ISO UTC timestamp; ``null`` = still pending. */
  readonly completed_at: string | null;
  readonly source: TaskSource;
  readonly source_ref: string | null;
  /**
   * Provenance of the completion: ``'user'`` (manual checkbox),
   * ``'agent'`` (nudge cascade), or ``'auto:<matcher_name>'``
   * (TaskAutoCompleter). ``null`` when not completed OR legacy.
   */
  readonly completed_by?: string | null;
  /**
   * Auto-completion evidence — present iff the row was marked by the
   * auto-completer. FE shows "Completato da Ando" badge + tooltip.
   */
  readonly completion_evidence?: TaskCompletionEvidence | null;
}

/**
 * Single-roundtrip envelope: the FE renders the two list sections AND the
 * header counts from one response, so the badge stays in sync with rows.
 */
export interface TasksTodayResponse {
  readonly pending: readonly TaskItem[];
  /** Pending tasks due beyond the next 24h — the "In arrivo" section. */
  readonly upcoming: readonly TaskItem[];
  readonly completed_today: readonly TaskItem[];
  readonly overdue_count: number;
  /** ``null`` when no pending row carries an ``estimated_minutes`` value. */
  readonly estimated_total_minutes: number | null;
}

/** Body for ``POST /tasks`` — every field but ``title`` is optional. */
export interface CreateTaskInput {
  readonly title: string;
  readonly notes?: string | null;
  readonly priority?: TaskPriority;
  /** ISO UTC timestamp. */
  readonly due_at?: string | null;
  readonly estimated_minutes?: number | null;
  readonly source?: TaskSource;
  readonly source_ref?: string | null;
}

/**
 * GET ``/tasks/today?limit=50`` — pending + completed-today + counts.
 *
 * The BE caps ``limit`` at 200 server-side; the default (50) matches the
 * inbox feed default.
 */
export async function listTodayTasks(limit = 50): Promise<TasksTodayResponse> {
  const qs = limit !== 50 ? `?limit=${limit}` : "";
  return await apiClient.get<TasksTodayResponse>(`/tasks/today${qs}`);
}

/** POST ``/tasks/{task_id}/complete`` — idempotent. */
export async function completeTask(taskId: string): Promise<void> {
  if (!taskId) throw new Error("completeTask: taskId required");
  await apiClient.post<void>(
    `/tasks/${encodeURIComponent(taskId)}/complete`,
  );
}

/** POST ``/tasks/{task_id}/uncomplete`` — restore to pending. */
export async function uncompleteTask(taskId: string): Promise<void> {
  if (!taskId) throw new Error("uncompleteTask: taskId required");
  await apiClient.post<void>(
    `/tasks/${encodeURIComponent(taskId)}/uncomplete`,
  );
}

/** POST ``/tasks`` — create a new task. */
export async function createTask(input: CreateTaskInput): Promise<TaskItem> {
  if (!input.title || !input.title.trim()) {
    throw new Error("createTask: title required");
  }
  return await apiClient.post<TaskItem>("/tasks", input);
}

/**
 * Body for ``PATCH /tasks/{task_id}`` — partial update.
 *
 * Every field is optional; omitted fields are left untouched server-side.
 * ``due_at: null`` is a legitimate "clear the snooze" signal (NOT
 * omission), so callers must intentionally pass ``null`` to clear.
 * ``is_deleted: true`` dismisses the task (soft-delete); the BE returns
 * a stub TaskItem since the FE never re-renders a dismissed row.
 */
export interface PatchTaskInput {
  readonly title?: string;
  readonly notes?: string | null;
  readonly priority?: TaskPriority;
  /** ISO UTC timestamp; ``null`` clears the existing snooze. */
  readonly due_at?: string | null;
  readonly estimated_minutes?: number | null;
  readonly is_deleted?: boolean;
}

/** PATCH ``/tasks/{task_id}`` — snooze / repriority / dismiss. */
export async function patchTask(
  taskId: string,
  input: PatchTaskInput,
): Promise<TaskItem> {
  if (!taskId) throw new Error("patchTask: taskId required");
  return await apiClient.patch<TaskItem>(
    `/tasks/${encodeURIComponent(taskId)}`,
    input,
  );
}

/** Counter envelope returned by ``POST /tasks/auto-complete``. */
export interface AutoCompleteTasksResponse {
  readonly candidate_tasks: number;
  readonly completed: number;
  readonly matcher_breakdown: Readonly<Record<string, number>>;
}

/** POST ``/tasks/auto-complete`` — synchronous trigger for the current user.
 *
 * Scans the user's active AI-source tasks and auto-marks the ones with
 * strong evidence of completion (matchers: ``due_date_passed`` +
 * ``llm_inference``). Returns counters so the FE can show a toast
 * ("Ando ha chiuso 3 task"). FE typically calls this on the Work hub
 * mount + every ``N`` minutes via TanStack polling.
 */
export async function autoCompleteTasks(): Promise<AutoCompleteTasksResponse> {
  return await apiClient.post<AutoCompleteTasksResponse>("/tasks/auto-complete");
}
